import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useNavigationType } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Chip,
  Alert,
  Container,
  useMediaQuery,
  useTheme,
  Grid,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
  Switch,
  FormControlLabel,
  Pagination,
  InputAdornment,
} from '@mui/material';
import {
  CalendarToday,
} from '@mui/icons-material';
import { ReactComponent as SearchIcon } from '../assets/icons/search.svg';
import SvgIcon from '@mui/material/SvgIcon';
import { ReactComponent as JobManagementPageIcon } from '../assets/icons/job-management-page.svg';
import { format, subMonths } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useAuth } from '../auth/AuthContext';
import PageHeader from '../components/PageHeader';
import { RankStarInline } from '../components/RankStar';
import { jobShiftApi } from '../services/api';
import { calcTotalPayment } from '../utils/salaryUtils';

interface JobPosting {
  id: string;
  job_id?: string;
  title: string;
  position_name?: string;
  hourly_wage: number;
  start_date?: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  working_hours?: number;
  required_workers: number;
  current_applicants: number;
  is_active: boolean;
  status: 'open' | 'closed' | 'draft';
  created_at: string;
  description?: string;
  admin_label?: string;
  salary_type?: string;
  salary_allowance?: string;
  salary_transport?: string;
  break_duration_minutes?: number;
  tier_code?: string;
}

/** 応募数が募集人数に達しているか。一覧・詳細モーダルで同じ判定を使う。 */
const isFull = (job: { current_applicants: number; required_workers: number }): boolean =>
  job.current_applicants >= job.required_workers;

const calcWorkingHours = (startTime?: string, endTime?: string): string => {
  if (!startTime || !endTime) return '--h';
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  let diffMin = (eh * 60 + em) - (sh * 60 + sm);
  if (diffMin <= 0) diffMin += 24 * 60; // 日付跨ぎ対応
  const hours = diffMin / 60;
  return `${hours.toFixed(1)}h`;
};

const JobManagementPage: React.FC = () => {
  const { storeId } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  // ブラウザ戻る/navigate(-1) で戻ってきた時のみ tab/page/scroll を復元する
  // サイドバー経由での再訪(PUSH)では sessionStorage の残骸を無視する
  const navigationType = useNavigationType();
  const isBackNavigation = navigationType === 'POP';

  const [jobPostings, setJobPostings] = useState<JobPosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [selectedJob, setSelectedJob] = useState<JobPosting | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    shift_date: '',
    start_time: '',
    end_time: '',
    required_workers: 0,
    is_active: true,
    break_duration_minutes: 0,
  });
  const [saving, setSaving] = useState(false);

  const CACHE_KEY = 'jobManagement_cache';
  const SCROLL_KEY = 'jobManagement_scrollY';
  const TAB_KEY = 'jobManagement_tab';
  const PAGE_KEY = 'jobManagement_pages';

  const readSavedPage = (key: 'active' | 'closed' | 'draft'): number => {
    if (!isBackNavigation) return 0;
    try {
      const saved = sessionStorage.getItem(PAGE_KEY);
      return saved ? (JSON.parse(saved)[key] ?? 0) : 0;
    } catch {
      return 0;
    }
  };

  const [activePage, setActivePage] = useState(() => readSavedPage('active'));
  const [closedPage, setClosedPage] = useState(() => readSavedPage('closed'));
  const [draftPage, setDraftPage] = useState(() => readSavedPage('draft'));
  const ROWS_PER_PAGE = 10;

  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const mapShiftsToPostings = (shifts: any[]): JobPosting[] => {
    return shifts.map((shift: any) => {
      const status: 'open' | 'closed' | 'draft' = shift.status
        ? shift.status
        : shift.is_active ? 'open' : 'closed';
      return {
        id: shift.id,
        job_id: shift.job_id || undefined,
        title: shift.job_title || 'シフト',
        hourly_wage: shift.salary_amount || 0,
        start_date: shift.shift_date,
        start_time: shift.start_time,
        end_time: shift.end_time,
        required_workers: shift.required_workers,
        current_applicants: shift.current_applicants,
        is_active: shift.is_active,
        status,
        created_at: shift.shift_date,
        description: shift.job_description,
        admin_label: shift.admin_label,
        salary_type: shift.salary_type,
        salary_allowance: shift.salary_allowance,
        salary_transport: shift.salary_transport,
        break_duration_minutes: shift.break_duration_minutes ?? 0,
      };
    });
  };

  const fetchJobPostings = useCallback(async (
    params: { from_date?: string; to_date?: string; search?: string } = {}
  ) => {
    if (!storeId) return;

    // 検索条件がある場合はキャッシュをスキップ
    const hasSearchParams = params.from_date || params.to_date || params.search;

    if (!hasSearchParams) {
      // キャッシュからデータを復元（戻るボタン対応）
      const cached = sessionStorage.getItem(CACHE_KEY);
      const cachedTab = sessionStorage.getItem(TAB_KEY);
      if (cached) {
        try {
          setJobPostings(JSON.parse(cached));
          if (isBackNavigation && cachedTab) setCurrentTab(Number(cachedTab));
          setLoading(false);
          // キャッシュ表示後、バックグラウンドで最新データを取得
          const data = await jobShiftApi.getWithApplicants(storeId, params);
          const mapped = mapShiftsToPostings(data.shifts);
          setJobPostings(mapped);
          sessionStorage.setItem(CACHE_KEY, JSON.stringify(mapped));
          return;
        } catch {
          // キャッシュ破損時は通常フローへ
        }
      }
    }

    try {
      setLoading(true);
      const data = await jobShiftApi.getWithApplicants(storeId, params);
      const mapped = mapShiftsToPostings(data.shifts);
      setJobPostings(mapped);
      if (!hasSearchParams) {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(mapped));
      }
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch job postings:', err);
      setError('求人の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchJobPostings();
  }, [storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    setActivePage(0); setClosedPage(0); setDraftPage(0);
    fetchJobPostings({
      from_date: dateFrom || undefined,
      to_date: dateTo || undefined,
      search: searchQuery.trim() || undefined,
    });
  };

  const handleReset = () => {
    setSearchQuery(''); setDateFrom(''); setDateTo('');
    setActivePage(0); setClosedPage(0); setDraftPage(0);
    fetchJobPostings({});
  };

  // スクロール位置の復元（戻る遷移時のみ）
  useEffect(() => {
    if (!loading && jobPostings.length > 0 && isBackNavigation) {
      const savedScrollY = sessionStorage.getItem(SCROLL_KEY);
      if (savedScrollY) {
        requestAnimationFrame(() => {
          window.scrollTo(0, Number(savedScrollY));
          sessionStorage.removeItem(SCROLL_KEY);
        });
      }
    }
  }, [loading, jobPostings, isBackNavigation]);

  // サイドバー等 PUSH 遷移でマウントされた場合は、応募者画面遷移時に
  // 残った sessionStorage を掃除する（次回以降の誤復元防止）
  useEffect(() => {
    if (!isBackNavigation) {
      sessionStorage.removeItem(SCROLL_KEY);
      sessionStorage.removeItem(TAB_KEY);
      sessionStorage.removeItem(PAGE_KEY);
    }
    // マウント時1回のみ実行
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (jobId: string) => {
    const job = jobPostings.find(j => j.id === jobId);
    if (job) {
      setSelectedJob(job);
      setDetailModalOpen(true);
    }
  };

  // TODO: 下書き機能実装時に有効化する
  // const handleDelete = (jobId: string) => {
  //   console.log('Delete job:', jobId);
  //   // 削除確認ダイアログを表示
  // };

  const handleView = (jobId: string) => {
    const job = jobPostings.find(j => j.id === jobId);
    if (job) {
      setSelectedJob(job);
      setDetailModalOpen(true);
    }
  };

  const handleViewApplicants = (jobId: string) => {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    sessionStorage.setItem(TAB_KEY, String(currentTab));
    sessionStorage.setItem(PAGE_KEY, JSON.stringify({ active: activePage, closed: closedPage, draft: draftPage }));
    navigate(`/applicants?shiftId=${jobId}`);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedJob(null);
    setIsEditMode(false);
  };

  const handleStartEdit = () => {
    if (!selectedJob) return;
    setEditForm({
      shift_date: selectedJob.start_date || selectedJob.created_at || '',
      start_time: selectedJob.start_time || '',
      end_time: selectedJob.end_time || '',
      required_workers: selectedJob.required_workers,
      is_active: selectedJob.is_active,
      break_duration_minutes: selectedJob.break_duration_minutes ?? 0,
    });
    setIsEditMode(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedJob || !storeId) return;
    try {
      setSaving(true);
      await jobShiftApi.update(selectedJob.id, {
        shift_date: editForm.shift_date,
        start_time: editForm.start_time,
        end_time: editForm.end_time,
        required_workers: editForm.required_workers,
        is_active: editForm.is_active,
        break_duration_minutes: editForm.break_duration_minutes,
      }, storeId);
      // データを再取得して反映
      const data = await jobShiftApi.getWithApplicants(storeId);
      const mapped = mapShiftsToPostings(data.shifts);
      setJobPostings(mapped);
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(mapped));
      // 更新後のジョブで選択を更新
      const updatedJob = mapped.find(j => j.id === selectedJob.id);
      setSelectedJob(updatedJob || null);
      setIsEditMode(false);
    } catch (err: any) {
      console.error('Failed to update job:', err);
      setError('求人の更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // 募集中は直近の日付を上に表示したいので、APIのDESC取得結果をASCに並べ直す
  const activeJobs = jobPostings
    .filter(job => job.status === 'open')
    .slice()
    .sort((a, b) => {
      const dateCmp = (a.start_date || '').localeCompare(b.start_date || '');
      if (dateCmp !== 0) return dateCmp;
      return (a.start_time || '').localeCompare(b.start_time || '');
    });
  const oneMonthAgo = subMonths(new Date(), 1);
  const closedJobs = jobPostings.filter(job => job.status === 'closed' && new Date(job.created_at) >= oneMonthAgo);
  const draftJobs = jobPostings.filter(job => job.status === 'draft');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PageHeader
        title="求人管理"
        icon={<SvgIcon component={JobManagementPageIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />}
      />

      {/* メインコンテンツ */}
      <Container maxWidth="lg" sx={{ pb: { xs: 8, sm: 4 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* タブ */}
        <Box
          sx={{
            display: 'flex',
            bgcolor: 'white',
            border: '1px solid #e7e7e7',
            borderRadius: '10px',
            overflow: 'hidden',
            mb: 3,
          }}
        >
          {['募集中', '終了', '下書き'].map((label, index) => (
            <Box
              key={label}
              onClick={() => setCurrentTab(index)}
              sx={{
                flex: 1,
                py: '10px',
                textAlign: 'center',
                borderRadius: '8px',
                cursor: 'pointer',
                bgcolor: currentTab === index ? '#f0f0f0' : 'transparent',
                transition: 'background-color 0.2s',
                userSelect: 'none',
              }}
            >
              <Typography
                variant="body2"
                fontWeight={currentTab === index ? 'bold' : 'normal'}
                sx={{ color: currentTab === index ? '#3c3c3c' : '#888' }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* 検索エリア */}
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          <TextField
            size="small"
            placeholder="タイトル・管理ラベルで検索"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIcon component={SearchIcon} inheritViewBox sx={{ color: '#898989', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            inputProps={{ style: { textOverflow: 'ellipsis' } }}
            fullWidth
            sx={{ bgcolor: 'white' }}
          />
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              size="small"
              label="勤務日（開始）"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ bgcolor: 'white', flex: 1 }}
            />
            <TextField
              size="small"
              label="勤務日（終了）"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ bgcolor: 'white', flex: 1 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="contained" size="small" onClick={handleSearch} sx={{ flex: 1 }}>検索</Button>
            <Button variant="outlined" size="small" onClick={handleReset} sx={{ flex: 1 }}>リセット</Button>
          </Box>
        </Stack>

        {/* 募集中タブ */}
        {currentTab === 0 && (
          <Box>
            {activeJobs.length === 0 ? (
              <Alert severity="info">現在募集中の求人はありません</Alert>
            ) : (
              <Grid container spacing={2}>
                {activeJobs.slice(activePage * ROWS_PER_PAGE, activePage * ROWS_PER_PAGE + ROWS_PER_PAGE).map(job => {
                  const progressPercent = job.required_workers > 0
                    ? (job.current_applicants / job.required_workers) * 100
                    : 0;

                  return (
                    <Grid item xs={12} md={6} key={job.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          border: '2px solid #d9f1f0',
                          borderLeft: '2px solid #00a29a',
                          borderRadius: 2
                        }}
                      >
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Stack spacing={1.5}>
                            {/* ランクバッジ */}
                            {job.tier_code && (
                              <Box>
                                <RankStarInline tierCode={job.tier_code} size={18} />
                              </Box>
                            )}
                            {/* 上部：募集日とステータス */}
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <CalendarToday sx={{ fontSize: 14 }} color="action" />
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  {format(new Date(job.created_at), 'yyyy.MM.dd(E)', { locale: ja })}
                                </Typography>
                              </Stack>
                              <Chip
                                label="募集中"
                                size="small"
                                sx={{
                                  bgcolor: '#ccf0eb',
                                  color: '#0d9488',
                                  fontWeight: 'bold',
                                  fontSize: '0.7rem',
                                  height: 22
                                }}
                              />
                            </Stack>

                            {/* 中部：時刻情報ボックス + 求人タイトル */}
                            <Grid container spacing={1}>
                              {/* 左側：時刻情報ボックス */}
                              <Grid item xs="auto" sm="auto" sx={{ pt: '0 !important', pl: '0 !important' }}>
                                <Box
                                  sx={{
                                    bgcolor: '#f3f4f6',
                                    borderRadius: 2,
                                    py: { xs: 0.75, sm: 1.0 },
                                    px: { xs: 1.5, sm: 2.0 },
                                    textAlign: 'center'
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, lineHeight: 1.2 }}
                                  >
                                    {job.start_time || '--:--'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ my: '6px', lineHeight: 0, display: 'block', fontSize: { xs: '0.6rem', sm: '0.65rem' } }}>
                                    ▼
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, lineHeight: 1.2 }}
                                  >
                                    {job.end_time || '--:--'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mt: 0.2, display: 'block', fontSize: { xs: '0.75rem', sm: '0.9rem' }, lineHeight: 1 }}>
                                    {calcWorkingHours(job.start_time, job.end_time)}
                                  </Typography>
                                </Box>
                              </Grid>

                              {/* 右側：求人タイトル */}
                              <Grid item xs={9} sm={9.5} sx={{ pt: '0 !important' }}>
                                <Typography variant="body1" fontWeight="bold" color={'#3c3c3c'} sx={{ fontSize: { xs: '0.85rem', sm: '1.125rem' } }}>
                                  {job.title}
                                </Typography>
                                {job.admin_label && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      mt: 0.5,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 1,
                                      WebkitBoxOrient: 'vertical',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    {job.admin_label}
                                  </Typography>
                                )}
                                {(() => { const v = calcTotalPayment(job.hourly_wage, job.salary_type || 'hourly', job.start_time || '', job.end_time || '', job.break_duration_minutes ?? 0, job.salary_allowance, job.salary_transport); return v !== null ? <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>¥{v.toLocaleString()}</Typography> : null; })()}
                              </Grid>
                            </Grid>

                            {/* 募集状況（横幅一杯） */}
                            <Box>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  募集状況
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                  {/* 満員は色（バーのグラデーション）だけでなく文言でも示す。
                                      色覚特性に依存せず、詳細モーダルの表示とも揃える。 */}
                                  {isFull(job) && (
                                    <Chip
                                      label="満員"
                                      size="small"
                                      color="error"
                                      sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                                    />
                                  )}
                                  <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                                    {job.current_applicants}/{job.required_workers}名
                                  </Typography>
                                </Stack>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(progressPercent, 100)}
                                sx={{
                                  height: 8,
                                  borderRadius: 5,
                                  bgcolor: '#e5e7eb',
                                  '& .MuiLinearProgress-bar': {
                                    background: 'linear-gradient(to right, #F5A623, #F07746, #E53935)',
                                    borderRadius: 5
                                  }
                                }}
                              />
                            </Box>

                            {/* ボタン（横幅一杯） */}
                            <Stack direction="row" spacing={1.5}>
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={() => handleEdit(job.id)}
                                sx={{
                                  bgcolor: '#bfdbfe',
                                  color: '#1e40af',
                                  borderRadius: 3,
                                  fontWeight: 'bold',
                                  py: 0.5,
                                  fontSize: '0.8rem',
                                  boxShadow: 'none',
                                  '&:hover': {
                                    bgcolor: '#93c5fd',
                                    boxShadow: 'none'
                                  }
                                }}
                              >
                                詳細・編集
                              </Button>
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={() => handleViewApplicants(job.id)}
                                sx={{
                                  bgcolor: '#bfdbfe',
                                  color: '#1e40af',
                                  borderRadius: 3,
                                  fontWeight: 'bold',
                                  py: 0.5,
                                  fontSize: '0.8rem',
                                  boxShadow: 'none',
                                  '&:hover': {
                                    bgcolor: '#93c5fd',
                                    boxShadow: 'none'
                                  }
                                }}
                              >
                                応募者を見る
                              </Button>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
            {activeJobs.length > ROWS_PER_PAGE && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={Math.ceil(activeJobs.length / ROWS_PER_PAGE)}
                  page={activePage + 1}
                  onChange={(_, value) => { setActivePage(value - 1); window.scrollTo(0, 0); }}
                  color="primary"
                  size="small"
                  siblingCount={2}
                  boundaryCount={1}
                />
              </Box>
            )}
          </Box>
        )}

        {/* 終了タブ */}
        {currentTab === 1 && (
          <Box>
            {closedJobs.length === 0 ? (
              <Alert severity="info">終了した求人はありません</Alert>
            ) : (
              <>
              <Grid container spacing={2}>
                {closedJobs.slice(closedPage * ROWS_PER_PAGE, closedPage * ROWS_PER_PAGE + ROWS_PER_PAGE).map(job => {
                  const progressPercent = job.required_workers > 0
                    ? (job.current_applicants / job.required_workers) * 100
                    : 0;

                  return (
                    <Grid item xs={12} md={6} key={job.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderLeft: '4px solid #9ca3af',
                          borderRadius: 2,
                          opacity: 0.7
                        }}
                      >
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Stack spacing={1.5}>
                            {/* ランクバッジ */}
                            {job.tier_code && (
                              <Box>
                                <RankStarInline tierCode={job.tier_code} size={18} />
                              </Box>
                            )}
                            {/* 上部：募集日とステータス */}
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <CalendarToday sx={{ fontSize: 14 }} color="action" />
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  {format(new Date(job.created_at), 'yyyy.MM.dd(E)', { locale: ja })}
                                </Typography>
                              </Stack>
                              <Chip
                                label="募集終了"
                                size="small"
                                sx={{
                                  bgcolor: '#f3f4f6',
                                  color: '#6b7280',
                                  fontWeight: 'bold',
                                  fontSize: '0.7rem',
                                  height: 22
                                }}
                              />
                            </Stack>

                            {/* 中部：時刻情報ボックス + 求人タイトル */}
                            <Grid container spacing={1}>
                              <Grid item xs="auto" sm="auto" sx={{ pt: '0 !important', pl: '0 !important' }}>
                                <Box
                                  sx={{
                                    bgcolor: '#f3f4f6',
                                    borderRadius: 2,
                                    py: { xs: 0.75, sm: 1.0 },
                                    px: { xs: 1.5, sm: 2.0 },
                                    textAlign: 'center'
                                  }}
                                >
                                  <Typography variant="body2" fontWeight="bold" color="text.secondary" sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, lineHeight: 1.2 }}>
                                    {job.start_time || '--:--'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ my: '6px', lineHeight: 0, display: 'block', fontSize: { xs: '0.6rem', sm: '0.65rem' } }}>
                                    ▼
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold" color="text.secondary" sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, lineHeight: 1.2 }}>
                                    {job.end_time || '--:--'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mt: 0.2, display: 'block', fontSize: { xs: '0.75rem', sm: '0.9rem' }, lineHeight: 1 }}>
                                    {calcWorkingHours(job.start_time, job.end_time)}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={9} sm={9.5} sx={{ pt: '0 !important' }}>
                                <Typography variant="body1" fontWeight="bold" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '1.125rem' } }}>
                                  {job.title}
                                </Typography>
                                {job.admin_label && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', fontSize: '0.75rem' }}>
                                    {job.admin_label}
                                  </Typography>
                                )}
                                {(() => { const v = calcTotalPayment(job.hourly_wage, job.salary_type || 'hourly', job.start_time || '', job.end_time || '', job.break_duration_minutes ?? 0, job.salary_allowance, job.salary_transport); return v !== null ? <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>¥{v.toLocaleString()}</Typography> : null; })()}
                              </Grid>
                            </Grid>

                            {/* 募集状況 */}
                            <Box>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  募集状況
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                  {/* 満員は色（バーのグラデーション）だけでなく文言でも示す。
                                      色覚特性に依存せず、詳細モーダルの表示とも揃える。 */}
                                  {isFull(job) && (
                                    <Chip
                                      label="満員"
                                      size="small"
                                      color="error"
                                      sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                                    />
                                  )}
                                  <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                    {job.current_applicants}/{job.required_workers}名
                                  </Typography>
                                </Stack>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(progressPercent, 100)}
                                sx={{
                                  height: 8,
                                  borderRadius: 5,
                                  bgcolor: '#e5e7eb',
                                  '& .MuiLinearProgress-bar': { bgcolor: '#9ca3af', borderRadius: 5 }
                                }}
                              />
                            </Box>

                            {/* ボタン */}
                            <Stack direction="row" spacing={1.5}>
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={() => handleView(job.id)}
                                sx={{ bgcolor: '#e5e7eb', color: '#6b7280', borderRadius: 3, fontWeight: 'bold', py: 0.5, fontSize: '0.8rem', boxShadow: 'none', '&:hover': { bgcolor: '#d1d5db', boxShadow: 'none' } }}
                              >
                                詳細
                              </Button>
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={() => handleViewApplicants(job.id)}
                                sx={{ bgcolor: '#e5e7eb', color: '#6b7280', borderRadius: 3, fontWeight: 'bold', py: 0.5, fontSize: '0.8rem', boxShadow: 'none', '&:hover': { bgcolor: '#d1d5db', boxShadow: 'none' } }}
                              >
                                応募者を見る
                              </Button>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </>
            )}
            {closedJobs.length > ROWS_PER_PAGE && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={Math.ceil(closedJobs.length / ROWS_PER_PAGE)}
                  page={closedPage + 1}
                  onChange={(_, value) => { setClosedPage(value - 1); window.scrollTo(0, 0); }}
                  color="primary"
                  size="small"
                  siblingCount={2}
                  boundaryCount={1}
                />
              </Box>
            )}
          </Box>
        )}

        {/* 下書きタブ */}
        {currentTab === 2 && (
          <Box>
            {draftJobs.length === 0 ? (
              <Alert severity="info">下書きの求人はありません</Alert>
            ) : (
              <Grid container spacing={2}>
                {draftJobs.slice(draftPage * ROWS_PER_PAGE, draftPage * ROWS_PER_PAGE + ROWS_PER_PAGE).map(job => (
                  <Grid item xs={12} md={6} key={job.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderLeft: '4px solid #f59e0b',
                        borderRadius: 2,
                        opacity: 0.85
                      }}
                    >
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack spacing={1.5}>
                          {/* ランクバッジ */}
                          {job.tier_code && (
                            <Box>
                              <RankStarInline tierCode={job.tier_code} size={18} />
                            </Box>
                          )}
                          {/* 上部：作成日とステータス */}
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <CalendarToday sx={{ fontSize: 14 }} color="action" />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {format(new Date(job.created_at), 'yyyy.MM.dd(E)', { locale: ja })}
                              </Typography>
                            </Stack>
                            <Chip
                              label="下書き"
                              size="small"
                              sx={{
                                bgcolor: '#fef3c7',
                                color: '#b45309',
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                height: 22
                              }}
                            />
                          </Stack>

                          {/* 中部：時刻情報ボックス + 求人タイトル */}
                          <Grid container spacing={1}>
                            <Grid item xs="auto" sm="auto" sx={{ pt: '0 !important', pl: '0 !important' }}>
                              <Box
                                sx={{
                                  bgcolor: '#f3f4f6',
                                  borderRadius: 2,
                                  py: { xs: 0.75, sm: 1.0 },
                                  px: { xs: 1.5, sm: 2.0 },
                                  textAlign: 'center'
                                }}
                              >
                                <Typography variant="body2" fontWeight="bold" color="text.secondary" sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, lineHeight: 1.2 }}>
                                  {job.start_time || '--:--'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ my: '6px', lineHeight: 0, display: 'block', fontSize: { xs: '0.6rem', sm: '0.65rem' } }}>
                                  ▼
                                </Typography>
                                <Typography variant="body2" fontWeight="bold" color="text.secondary" sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, lineHeight: 1.2 }}>
                                  {job.end_time || '--:--'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mt: 0.2, display: 'block', fontSize: { xs: '0.75rem', sm: '0.9rem' }, lineHeight: 1 }}>
                                  {calcWorkingHours(job.start_time, job.end_time)}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={9} sm={9.5} sx={{ pt: '0 !important' }}>
                              <Typography variant="body1" fontWeight="bold" color={'#3c3c3c'} sx={{ fontSize: { xs: '0.85rem', sm: '1.125rem' } }}>
                                {job.title}
                              </Typography>
                              {job.admin_label && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', fontSize: '0.75rem' }}>
                                  {job.admin_label}
                                </Typography>
                              )}
                              {(() => { const v = calcTotalPayment(job.hourly_wage, job.salary_type || 'hourly', job.start_time || '', job.end_time || '', job.break_duration_minutes ?? 0, job.salary_allowance, job.salary_transport); return v !== null ? <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>¥{v.toLocaleString()}</Typography> : null; })()}
                            </Grid>
                          </Grid>

                          {/* ボタン */}
                          <Stack direction="row" spacing={1.5}>
                            <Button
                              variant="contained"
                              fullWidth
                              onClick={() => handleEdit(job.id)}
                              sx={{ bgcolor: '#fef3c7', color: '#b45309', borderRadius: 3, fontWeight: 'bold', py: 0.5, fontSize: '0.8rem', boxShadow: 'none', '&:hover': { bgcolor: '#fde68a', boxShadow: 'none' } }}
                            >
                              詳細・編集
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            {draftJobs.length > ROWS_PER_PAGE && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={Math.ceil(draftJobs.length / ROWS_PER_PAGE)}
                  page={draftPage + 1}
                  onChange={(_, value) => { setDraftPage(value - 1); window.scrollTo(0, 0); }}
                  color="primary"
                  size="small"
                  siblingCount={2}
                  boundaryCount={1}
                />
              </Box>
            )}
          </Box>
        )}
      </Container>

      {/* 求人詳細モーダル */}
      <Dialog
        open={detailModalOpen}
        onClose={handleCloseDetailModal}
        maxWidth="md"
        fullWidth
      >
        {selectedJob && (
          <>
            <DialogTitle>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                  {isEditMode ? '求人編集' : '求人詳細'}
                </Typography>
                {isEditMode && selectedJob.status === 'draft' ? (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editForm.is_active}
                        onChange={(e) => setEditForm(prev => ({ ...prev, is_active: e.target.checked }))}
                      />
                    }
                    label={editForm.is_active ? '募集中' : '募集終了'}
                  />
                ) : (
                  <Chip
                    label={selectedJob.status === 'open' ? '募集中' : selectedJob.status === 'draft' ? '下書き' : '募集終了'}
                    size="small"
                    color={selectedJob.status === 'open' ? 'success' : selectedJob.status === 'draft' ? 'warning' : 'default'}
                  />
                )}
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={3}>
                {/* 求人タイトル */}
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    求人タイトル
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedJob.title}
                  </Typography>
                </Box>

                <Divider />

                {/* 勤務日 */}
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    勤務日
                  </Typography>
                  {isEditMode && selectedJob.status === 'draft' ? (
                    <TextField
                      type="date"
                      value={editForm.shift_date}
                      onChange={(e) => setEditForm(prev => ({ ...prev, shift_date: e.target.value }))}
                      fullWidth
                      size="small"
                    />
                  ) : (
                    <Typography variant="body1">
                      {format(new Date(selectedJob.created_at), 'yyyy年MM月dd日 (E)', { locale: ja })}
                    </Typography>
                  )}
                </Box>

                {/* 勤務時間 */}
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    勤務時間
                  </Typography>
                  {isEditMode && selectedJob.status === 'draft' ? (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <TextField
                        type="time"
                        value={editForm.start_time}
                        onChange={(e) => setEditForm(prev => ({ ...prev, start_time: e.target.value }))}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <Typography>〜</Typography>
                      <TextField
                        type="time"
                        value={editForm.end_time}
                        onChange={(e) => setEditForm(prev => ({ ...prev, end_time: e.target.value }))}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body1">
                        {selectedJob.start_time || '--:--'} 〜 {selectedJob.end_time || '--:--'}
                      </Typography>
                      <Chip
                        label={(() => { const h = calcWorkingHours(selectedJob.start_time, selectedJob.end_time); return h === '--h' ? '--時間' : h.replace('h', '時間'); })()}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                  )}
                </Box>

                {/* 募集期間 */}
                {!isEditMode && selectedJob.start_date && selectedJob.end_date && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      募集期間
                    </Typography>
                    <Typography variant="body1">
                      {format(new Date(selectedJob.start_date), 'yyyy年MM月dd日', { locale: ja })} 〜{' '}
                      {format(new Date(selectedJob.end_date), 'yyyy年MM月dd日', { locale: ja })}
                    </Typography>
                  </Box>
                )}

                {/* 時給 */}
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    時給
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    ¥{selectedJob.hourly_wage.toLocaleString()}
                  </Typography>
                </Box>

                {/* 休憩時間・交通費・手当・合計 */}
                {isEditMode && selectedJob.status === 'draft' ? (
                  <>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        休憩時間（分）
                      </Typography>
                      <TextField
                        type="number"
                        value={editForm.break_duration_minutes}
                        onChange={(e) => setEditForm(prev => ({ ...prev, break_duration_minutes: parseInt(e.target.value) || 0 }))}
                        size="small"
                        inputProps={{ min: 0, step: 15 }}
                        fullWidth
                      />
                    </Box>
                    {(selectedJob.salary_transport || selectedJob.salary_allowance) && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          交通費・手当（変更不可）
                        </Typography>
                        <Stack direction="row" spacing={2}>
                          {selectedJob.salary_transport && (
                            <Typography variant="body2">交通費：¥{(Number(selectedJob.salary_transport) || 0).toLocaleString()}</Typography>
                          )}
                          {selectedJob.salary_allowance && (
                            <Typography variant="body2">手当：¥{(Number(selectedJob.salary_allowance) || 0).toLocaleString()}</Typography>
                          )}
                        </Stack>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        合計（目安）
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {(() => { const v = calcTotalPayment(selectedJob.hourly_wage, selectedJob.salary_type || 'hourly', editForm.start_time || '', editForm.end_time || '', editForm.break_duration_minutes, selectedJob.salary_allowance, selectedJob.salary_transport); return v !== null ? `¥${v.toLocaleString()}` : '--'; })()}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    {!!selectedJob.break_duration_minutes && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          休憩時間
                        </Typography>
                        <Typography variant="body1">{selectedJob.break_duration_minutes}分</Typography>
                      </Box>
                    )}
                    {(selectedJob.salary_transport || selectedJob.salary_allowance) && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          交通費・手当
                        </Typography>
                        <Stack direction="row" spacing={2}>
                          {selectedJob.salary_transport && (
                            <Typography variant="body2">交通費：¥{(Number(selectedJob.salary_transport) || 0).toLocaleString()}</Typography>
                          )}
                          {selectedJob.salary_allowance && (
                            <Typography variant="body2">手当：¥{(Number(selectedJob.salary_allowance) || 0).toLocaleString()}</Typography>
                          )}
                        </Stack>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        合計
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {(() => { const v = calcTotalPayment(selectedJob.hourly_wage, selectedJob.salary_type || 'hourly', selectedJob.start_time || '', selectedJob.end_time || '', selectedJob.break_duration_minutes ?? 0, selectedJob.salary_allowance, selectedJob.salary_transport); return v !== null ? `¥${v.toLocaleString()}` : '--'; })()}
                      </Typography>
                    </Box>
                  </>
                )}

                {/* 募集状況 */}
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    募集人数
                  </Typography>
                  {isEditMode ? (
                    <TextField
                      type="number"
                      value={editForm.required_workers}
                      onChange={(e) => setEditForm(prev => ({ ...prev, required_workers: parseInt(e.target.value) || 0 }))}
                      size="small"
                      inputProps={{ min: 1 }}
                      fullWidth
                    />
                  ) : (
                    <>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="body1" fontWeight="bold">
                          {selectedJob.current_applicants}/{selectedJob.required_workers}名
                        </Typography>
                        {isFull(selectedJob) && (
                          <Chip label="満員" size="small" color="error" />
                        )}
                      </Stack>
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min((selectedJob.current_applicants / selectedJob.required_workers) * 100, 100)}
                          sx={{
                            height: 10,
                            borderRadius: 5,
                            bgcolor: '#e5e7eb',
                            '& .MuiLinearProgress-bar': {
                              background: selectedJob.is_active
                                ? 'linear-gradient(to right, #F5A623, #F07746, #E53935)'
                                : '#9ca3af',
                              borderRadius: 5
                            }
                          }}
                        />
                      </Box>
                    </>
                  )}
                </Box>

                {/* ポジション */}
                {selectedJob.position_name && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      ポジション
                    </Typography>
                    <Chip label={selectedJob.position_name} />
                  </Box>
                )}

                {/* 募集メモ */}
                {selectedJob.admin_label && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      募集メモ
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedJob.admin_label}
                    </Typography>
                  </Box>
                )}

                {/* 説明 */}
                {selectedJob.description && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      説明
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedJob.description}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              {isEditMode ? (
                <>
                  <Button onClick={() => setIsEditMode(false)} color="inherit" disabled={saving}>
                    キャンセル
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveEdit}
                    disabled={saving}
                  >
                    {saving ? '保存中...' : '保存する'}
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={handleCloseDetailModal} color="inherit">
                    閉じる
                  </Button>
                  {selectedJob.status !== 'closed' && (
                    <Button
                      variant="contained"
                      onClick={handleStartEdit}
                    >
                      編集する
                    </Button>
                  )}
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default JobManagementPage;
